/*
  QC4: one validation + messaging layer for every Shopify form in the theme (newsletter footer/popup/password page,
  download guide, contact, partner inquiries, login/recover/register/reset/activate, addresses).

  1. Inline errors. Forms get `novalidate` (set here, so without JS the browser bubbles still work) and are checked on submit:
     required, email format, minlength, data-srx-match (confirm password). Each problem is shown as a small message under its
     field (aria-invalid + aria-describedby), and the first bad field gets focus. Mobile Safari shows no native bubble at all.
  2. Server errors. snippets/srx-form-errors renders Shopify's form.errors with data-field keys; each line moves under its field.
  3. Newsletter forms (data-srx-ajax) post in the background and show their thank-you line in place, so the page no longer
     reloads and jumps. Shopify's hCaptcha still runs: it wraps form.submit(), which is replaced here before it binds.
     Anything unexpected (challenge page, network error) falls back to a normal submit.
*/
(function () {
  'use strict';
  if (window.srxForms) return;

  var MSG = Object.assign({
    required: 'Please fill in this field.',
    emailRequired: 'Please enter your email address.',
    email: 'Please enter a valid email address, like name@example.com.',
    minlength: 'Please use at least {n} characters.',
    match: 'The passwords don\u2019t match.',
    generic: 'Something went wrong. Please try again.'
  }, window.srxFormMessages || {});

  var FORMS = 'form[action^="/contact"], form[action^="/account"], form[action^="/password"]';
  var EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[^\s@.]{2,}$/;
  var uid = 0;

  function fields(form) {
    return Array.prototype.filter.call(form.elements, function (el) {
      return /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) && !el.disabled &&
        !/^(hidden|submit|button|reset|image)$/.test(el.type) && !el.closest('[hidden]');
    });
  }

  function problem(el) {
    var v = (el.value || '').trim();
    if (el.type === 'checkbox' || el.type === 'radio') return el.required && !el.checkValidity() ? MSG.required : '';
    if (el.required && !v) return el.type === 'email' ? MSG.emailRequired : MSG.required;
    if (!v) return '';
    if (el.type === 'email' && !EMAIL.test(v)) return MSG.email;
    if (el.minLength > 0 && el.value.length < el.minLength) return MSG.minlength.replace('{n}', el.minLength);
    var m = el.getAttribute('data-srx-match');
    if (m) { var other = el.form.querySelector('#' + m); if (other && other.value !== el.value) return MSG.match; }
    if (!el.checkValidity()) return el.validationMessage || MSG.required;
    return '';
  }

  /* single-field forms laid out in a row (download guide, password page) get the message as a full-width last line */
  function slot(el) {
    var row = el.closest('.guideform, .search-form');
    return row ? (row.querySelector('[data-srx-fields]') || row) : null;
  }

  function msgFor(el, create) {
    var id = el.getAttribute('data-srx-err');
    var p = id && document.getElementById(id);
    if (p || !create) return p;
    id = 'srx-ferr-' + (++uid);
    p = document.createElement('p');
    p.className = 'srx-ferr';
    p.id = id;
    el.setAttribute('data-srx-err', id);
    var host = slot(el);
    if (host) host.appendChild(p); else el.insertAdjacentElement('afterend', p);
    return p;
  }

  function describedBy(el, id, add) {
    var ids = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(function (x) { return x && x !== id; });
    if (add) ids.push(id);
    if (ids.length) el.setAttribute('aria-describedby', ids.join(' ')); else el.removeAttribute('aria-describedby');
  }

  function setError(el, text) {
    var p = msgFor(el, true);
    p.textContent = text;
    p.hidden = false;
    el.setAttribute('aria-invalid', 'true');
    describedBy(el, p.id, true);
  }

  function clearError(el) {
    var p = msgFor(el, false);
    el.removeAttribute('aria-invalid');
    if (p) { p.hidden = true; p.textContent = ''; describedBy(el, p.id, false); }
  }

  function validate(form) {
    var first = null;
    fields(form).forEach(function (el) {
      var text = problem(el);
      if (text) { setError(el, text); first = first || el; } else clearError(el);
    });
    return first;
  }

  /* Shopify field key (email, password, first_name...) -> the input it belongs to */
  function fieldByKey(form, key) {
    if (!key || key === 'form') return null;
    var list = form.querySelectorAll('[name="' + key + '"], [name$="[' + key + ']"]');
    for (var i = 0; i < list.length; i++) if (list[i].type !== 'hidden') return list[i];
    return null;
  }

  /* move each server error line under its field; lines without a field ('form', unknown keys) stay in the box */
  function placeServerErrors(form) {
    var box = form.querySelector('[data-srx-errors]');
    if (!box) return null;
    var first = null;
    var left = 0;
    box.querySelectorAll('li[data-field]').forEach(function (li) {
      var el = fieldByKey(form, li.getAttribute('data-field'));
      if (el) { setError(el, li.textContent.trim()); li.hidden = true; first = first || el; } else left++;
    });
    box.hidden = left === 0;
    return box.hidden ? first : box;
  }

  function busy(form, on) {
    var btn = form.querySelector('[type="submit"]');
    form.toggleAttribute('data-srx-busy', on);
    if (btn) { btn.disabled = on; btn.setAttribute('aria-busy', on ? 'true' : 'false'); }
  }

  /* success state: thank-you line visible, fields hidden (both are always rendered, see srx-footer / srx-popup) */
  function showOk(form, on) {
    var ok = form.querySelector('[data-srx-ok]');
    var flds = form.querySelector('[data-srx-fields]');
    if (!ok || !flds) return false;
    ok.hidden = !on;
    flds.hidden = on;
    if (on) { var t = ok.textContent; ok.textContent = ''; ok.textContent = t; } /* re-set so the live region is announced */
    return true;
  }

  function nativeSubmit(form) {
    HTMLFormElement.prototype.submit.call(form);
  }

  function ajaxSubmit(form) {
    if (form.hasAttribute('data-srx-busy')) return;
    if (validate(form)) { focusFirst(form); return; }
    busy(form, true);
    var action = (form.getAttribute('action') || '/contact').split('#')[0];
    fetch(action, {
      method: 'POST',
      body: new URLSearchParams(new FormData(form)),
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    }).then(function (res) {
      if (/\/challenge/.test(res.url) || !res.ok) throw new Error('fallback');
      return res.text().then(function (html) { return { url: res.url, html: html }; });
    }).then(function (r) {
      var doc = new DOMParser().parseFromString(r.html, 'text/html');
      var twin = form.id && doc.getElementById(form.id);
      var okTwin = twin && twin.querySelector('[data-srx-ok]:not([hidden])');
      var errTwin = twin && twin.querySelector('[data-srx-errors]');
      busy(form, false);
      if (errTwin && !okTwin) {
        var old = form.querySelector('[data-srx-errors]');
        var box = document.importNode(errTwin, true);
        if (old) old.replaceWith(box); else (form.querySelector('[data-srx-fields]') || form).prepend(box);
        focusTarget(placeServerErrors(form), false);
        return;
      }
      if (okTwin || /[?&](customer|contact)_posted=true/.test(r.url)) {
        var old2 = form.querySelector('[data-srx-errors]');
        if (old2) old2.remove();
        fields(form).forEach(clearError);
        form.reset();
        if (!showOk(form, true)) throw new Error('fallback');
        form.dispatchEvent(new CustomEvent('srx:subscribed', { bubbles: true }));
        return;
      }
      throw new Error('fallback');
    }).catch(function () {
      busy(form, false);
      nativeSubmit(form);
    });
  }

  function focusTarget(el, noScroll) {
    if (!el) return;
    if (!/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) el.tabIndex = -1;
    el.focus({ preventScroll: !!noScroll });
  }

  function focusFirst(form) {
    var el = form.querySelector('[aria-invalid="true"]');
    if (el) el.focus();
  }

  /* Live fix (Oct 15): background posting is OFF. Shopify's spam protection (hCaptcha) expects a normal form post; posting in
     the background gave "invalid captcha token" on the popup and a second captcha. Newsletter forms now post normally and the
     reload lands back on the form (unique ids), which shows its thank-you line. Set to true to bring background posting back. */
  var AJAX = false;

  function onSubmit(e) {
    var form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.matches(FORMS)) return;
    var sub = e.submitter;
    if (sub && sub.formNoValidate) return;
    var bad = validate(form);
    if (bad) {
      e.preventDefault();
      e.stopImmediatePropagation(); /* also keeps Shopify's hCaptcha from running on an invalid form */
      bad.focus();
      return;
    }
    if (!AJAX || !form.hasAttribute('data-srx-ajax')) return;
    /* hCaptcha bound: let it run; it ends by calling form.submit(), which is ajaxSubmit (installed in init) */
    if (form.hasAttribute('data-hcaptcha-bound')) return;
    e.preventDefault(); /* other submit listeners (popup snooze) still run */
    ajaxSubmit(form);
  }

  function onInput(e) {
    var el = e.target;
    if (!el || !el.form || el.getAttribute('aria-invalid') !== 'true') return;
    if (!problem(el)) clearError(el);
    var mate = el.id && el.form.querySelector('[data-srx-match="' + el.id + '"][aria-invalid="true"]');
    if (mate && !problem(mate)) clearError(mate);
  }

  function init(form) {
    if (form.hasAttribute('data-srx-forms')) return;
    form.setAttribute('data-srx-forms', '');
    form.noValidate = true;
    if (AJAX && form.hasAttribute('data-srx-ajax') && window.fetch && window.DOMParser && window.URLSearchParams) {
      try { form.submit = function () { ajaxSubmit(form); }; } catch (err) {}
    }
    var target = placeServerErrors(form);
    var ok = form.querySelector('[data-srx-ok]:not([hidden])');
    var hashed = form.id && location.hash === '#' + form.id;
    /* after a normal (non-ajax) newsletter post Shopify marks every customer form on the page as posted; only the one
       the URL points at keeps its thank-you line */
    if (ok && form.hasAttribute('data-srx-ajax') && location.hash && !hashed) { showOk(form, false); ok = null; }
    if (target || ok) {
      var det = form.closest('details');
      if (det) det.open = true;
    }
    if (hashed && (target || ok)) {
      form.scrollIntoView({ block: 'center' });
      focusTarget(target, true);
    }
  }

  function initAll() { document.querySelectorAll(FORMS).forEach(init); }

  window.srxForms = { init: init, validate: validate, messages: MSG };
  document.addEventListener('touchstart', function () {}, { passive: true }); /* lets iOS Safari apply :active (button press colours) */
  document.addEventListener('submit', onSubmit, true);
  document.addEventListener('input', onInput);
  document.addEventListener('change', onInput);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll); else initAll();
})();
